import { differenceInCalendarDays, parseISO } from 'date-fns';

/**
 * For Open Leads: an urgency label derived from follow-up / waiting state.
 */
export const leadUrgency = (opts: {
  firstcontact_at: string | null;
  followup_at: string | null;
  waiting_since: string | null;
  now?: Date;
}): { label: string; tone: 'red' | 'amber' | 'green' } => {
  const now = opts.now ?? new Date();

  if (!opts.firstcontact_at) {
    return { label: 'New', tone: 'green' };
  }
  if (opts.waiting_since && parseISO(opts.waiting_since) < now) {
    const days = differenceInCalendarDays(now, parseISO(opts.waiting_since));
    return { label: `No reply ${days}d`, tone: 'amber' };
  }
  if (opts.followup_at) {
    const days = differenceInCalendarDays(now, parseISO(opts.followup_at));
    if (days > 0) return { label: `Overdue ${days}d`, tone: 'red' };
    if (days === 0) return { label: 'Due today', tone: 'amber' };
    return { label: `Due in ${-days}d`, tone: 'green' };
  }
  return { label: 'Open', tone: 'green' };
};

/**
 * For Past Customers / Prospects: days until (or since) the follow-up date.
 */
export const followupUrgency = (
  followup_at: string | null,
  now: Date = new Date(),
): { label: string; tone: 'red' | 'amber' | 'green' } => {
  if (!followup_at) return { label: '—', tone: 'green' };
  const days = differenceInCalendarDays(now, parseISO(followup_at));
  if (days > 0) return { label: `Overdue ${days}d`, tone: 'red' };
  if (days === 0) return { label: 'Due today', tone: 'amber' };
  return { label: `${-days}d to window`, tone: 'green' };
};

/**
 * Display name for a B2B customer card: organization name if present,
 * otherwise the person's name, falling back to a generic label. Shared by
 * Past Customer Outreach and Prospects (both render `OpportunityCustomer`).
 */
export const customerName = (customer: {
  first_name: string | null;
  last_name: string | null;
  organization_name: string | null;
}): string =>
  customer.organization_name ||
  `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim() ||
  'Customer';
